import { useState } from 'react';
import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { NumberInput } from '../ui/number-input';
import { SupplierProduct } from '../../lib/types';
import {
  Package,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { Badge } from '../ui/badge';

interface StockAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: SupplierProduct;
  onAdjust: (productId: string, type: 'add' | 'remove' | 'set', quantity: number, notes?: string) => Promise<void>;
}

export default function StockAdjustmentModal({
  isOpen,
  onClose,
  product,
  onAdjust
}: StockAdjustmentModalProps) {
  const [transactionType, setTransactionType] = useState<'add' | 'remove' | 'set'>('add');
  const [quantity, setQuantity] = useState<number>(1);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // Early return if product is not loaded
  if (!product) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (quantity <= 0) return;

    try {
      setLoading(true);
      await onAdjust(product._id, transactionType, quantity, notes);
      onClose();
      // Reset form
      setTransactionType('add');
      setQuantity(1);
      setNotes('');
    } catch (error) {
      console.error('Failed to adjust stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNewStockLevel = () => {
    const current = product?.inventory?.availableStock ?? 0;
    switch (transactionType) {
      case 'add': return current + quantity;
      case 'remove': return Math.max(0, current - quantity);
      case 'set': return quantity;
      default: return current;
    }
  };

  const newStock = getNewStockLevel();
  const isLowStock = newStock <= (product?.inventory?.lowStockThreshold ?? 10);
  const isOutOfStock = newStock === 0;

  return (
    <EnhancedDialog open={isOpen} onOpenChange={onClose}>
      <EnhancedDialogContent size="2xl">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Adjust Stock - {product?.product?.name || 'Product'}
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Update inventory levels for this product from {product?.supplier?.businessName || 'Supplier'}
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <EnhancedDialogBody>
          <form id="stock-adjustment-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Current Status */}
            <div className="bg-muted/50 p-4 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current Stock</p>
                <p className="text-2xl font-bold">{product?.inventory?.availableStock ?? 0}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant={
                  product?.inventory?.isOutOfStock ? 'destructive' :
                    product?.inventory?.isLowStock ? 'secondary' : 'default'
                } className={
                  product?.inventory?.isOutOfStock ? '' :
                    product?.inventory?.isLowStock ? 'bg-orange-100 text-orange-800 hover:bg-orange-100' :
                      'bg-green-100 text-green-800 hover:bg-green-100'
                }>
                  {product?.inventory?.isOutOfStock ? 'Out of Stock' :
                    product?.inventory?.isLowStock ? 'Low Stock' : 'In Stock'}
                </Badge>
              </div>
            </div>

            {/* Transaction Type */}
            <div className="grid grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => setTransactionType('add')}
                className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${transactionType === 'add'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-muted hover:border-green-200 hover:bg-green-50/50'
                  }`}
              >
                <ArrowUpCircle className="h-6 w-6" />
                <span className="font-medium">Add Stock</span>
              </button>
              <button
                type="button"
                onClick={() => setTransactionType('remove')}
                className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${transactionType === 'remove'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-muted hover:border-red-200 hover:bg-red-50/50'
                  }`}
              >
                <ArrowDownCircle className="h-6 w-6" />
                <span className="font-medium">Remove Stock</span>
              </button>
              <button
                type="button"
                onClick={() => setTransactionType('set')}
                className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${transactionType === 'set'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-muted hover:border-blue-200 hover:bg-blue-50/50'
                  }`}
              >
                <RefreshCw className="h-6 w-6" />
                <span className="font-medium">Set Quantity</span>
              </button>
            </div>

            {/* Quantity Input */}
            <div className="space-y-2">
              <Label>Quantity</Label>
              <NumberInput
                value={quantity}
                onChange={setQuantity}
                min={1}
                placeholder="Enter quantity"
                className="text-lg"
              />
              <p className="text-sm text-muted-foreground">
                {transactionType === 'add' ? 'Amount to add to current stock' :
                  transactionType === 'remove' ? 'Amount to remove from current stock' :
                    'New total stock quantity'}
              </p>
            </div>

            {/* Preview */}
            <div className="bg-muted p-4 rounded-lg border">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">New Stock Level Preview</span>
                <span className={`text-lg font-bold ${newStock > (product?.inventory?.availableStock ?? 0) ? 'text-green-600' :
                  newStock < (product?.inventory?.availableStock ?? 0) ? 'text-red-600' :
                    'text-foreground'
                  }`}>
                  {newStock}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {isOutOfStock ? (
                  <span className="flex items-center text-red-600">
                    <XCircle className="h-4 w-4 mr-1" /> Will be Out of Stock
                  </span>
                ) : isLowStock ? (
                  <span className="flex items-center text-orange-600">
                    <AlertTriangle className="h-4 w-4 mr-1" /> Will be Low Stock
                  </span>
                ) : (
                  <span className="flex items-center text-green-600">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Will be In Stock
                  </span>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Reason / Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., New shipment received, Damaged goods removed..."
                rows={3}
              />
            </div>
          </form>
        </EnhancedDialogBody>

        <EnhancedDialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="stock-adjustment-form"
            disabled={loading || quantity <= 0}
            className={
              transactionType === 'add' ? 'bg-green-600 hover:bg-green-700' :
                transactionType === 'remove' ? 'bg-red-600 hover:bg-red-700' :
                  'bg-blue-600 hover:bg-blue-700'
            }
          >
            {loading ? 'Updating...' : 'Update Stock'}
          </Button>
        </EnhancedDialogFooter>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
}